import { useEffect, useRef, useState } from 'react';
import styles from './index.module.less';
import { getPersionalProductApi, orderSubsRequest } from '@/lib/service';
import { useConfigProvider } from '@/context/ConfigProvider';
import { useUserProvider } from '@/context/UserProvider';
import { App, Button, Tooltip } from 'antd';
import { useRouter } from 'next/router';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';

const AddAccountModal = dynamic(() => import('@/components/protectedAccount/AddAcountModal'));
const PayFailModal = dynamic(() => import('@/components/Common/PayFailModal'));
const ConfirmUpgradeModal = dynamic(() => import('@/components/Common/ConfirmUpgradeModal'));
const NeedHelp = dynamic(() => import('./NeedHelp'));

// ✅ 优化：纯展示组件，不依赖 Stripe SDK
// eslint-disable-next-line no-undef
const Index: React.FC<any> = ({ data }) => {
  const { t } = useTranslation('common');

  // ✅ 优化：移除 Stripe hooks，改为手动调用 Stripe API
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const stripeRef = useRef<any>(null);
  const [tab, setTab] = useState(0);
  const [planList, setplanList] = useState<any[]>(data.planList ?? []);
  const [newPlanList, setnewPlanList] = useState<any[]>(
    (data.planList ?? []).filter((item: any) => item.data_type === 'Month'),
  );
  const [selectItem, setselectItem] = useState<any>({});
  const { isMobile } = useConfigProvider();
  const planContainer = useRef(null);
  const { isLogin, onOpenLoginModal, userUse } = useUserProvider();
  const client_secret = useRef('');
  const { message } = App.useApp();
  const router = useRouter();
  const [userSubs, setUserSubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // 升级确认弹窗
  const [isUpgradaModal, setIsUpgradeModal] = useState(false);
  const risk_unique_id = useSearchParams().get('risk_unique_id');
  const getBtndesc = (item: any) => {
    if (isLogin) {
      if (userSubs.length > 0) {
        if (userSubs[0]?.item_id == item.item_id) {
          if (new Date(userSubs[0]?.expire_time).getTime() < new Date().getTime()) {
            return t('Renew');
          } else {
            return t('Current plan');
          }
        } else {
          if (isChange(item.item_id)) {
            return t('Upgrade Protection');
          } else {
            return t('Get started today');
          }
        }
      } else {
        return t('Get started today');
      }
    } else {
      return t('Get started today');
    }
  };

  const getPlanDescription = (item: any) => {
    if (!item || !item.item_id) {
      return 'For checking leak risk only — no removals or protection included.';
    }
    // Trial 套餐
    if (item.item_id === 0 || item.name === 'Trial') {
      return 'For checking leak risk only — no removals or protection included.';
    }
    // Starter (item_id: 1 月付, 2 年付)
    if (item.item_id === 1 || item.item_id === 2) {
      return 'Best for personal privacy and creators just getting started.';
    }
    // Advanced (item_id: 3 月付, 4 年付)
    if (item.item_id === 3 || item.item_id === 4) {
      return 'Best value for creators with growing audiences and impersonation issues.';
    }
    // Elite (item_id: 5 月付, 6 年付)
    if (item.item_id === 5 || item.item_id === 6) {
      return 'Built for professional creators & teams who want total protection.';
    }
    return '';
  };
  useEffect(() => {
    if (isLogin) {
      getPersionalProductApi().then((res) => {
        setselectItem(res.data[0]);
        setUserSubs(res.data);
        if (res.data?.length > 0) {
          if ([1, 3, 5].indexOf(res.data[0]?.item_id) != -1) {
            setTab(0);
          } else {
            setTab(1);
          }
        } else {
          setselectItem(data.planList[0]);
        }
      });
    } else {
      setselectItem(data.planList[2]);
    }
  }, [isLogin, data.planList]);
  // 支付失败状态
  const [isPayFailModal, setIsPayFailModal] = useState(false);

  // ✅ 优化：动态加载 Stripe SDK（仅在用户点击支付时加载）
  const loadStripeSDK = async () => {
    if (stripeRef.current) {
      return stripeRef.current;
    }

    const { loadStripe } = await import('@stripe/stripe-js');
    const isProd = process.env.NODE_ENV === 'production' && !process.env.NEXT_APP_API_HOST.includes('-test.');
    const stripe = await loadStripe(
      isProd
        ? 'pk_live_51RWr2EQ6hZ8tbS8t6gscB3SGeJbrJ6wp7jXiCObw4UBbjYsVClhRGbBrAHZshnl8R58cLjRP5kcnPkK5ooAJ8fZX00xrpfMrDT'
        : 'pk_test_51RWr2EQ6hZ8tbS8tyLdVUpN3FoPpMZEDMjE7CHrBPYMh6QZ6c1DYlKGmLyKVSNgTlZJE1fy0OkyGzlXQqRKS3EpG00sJiZkuLB',
    );
    stripeRef.current = stripe;
    setStripeLoaded(true);
    return stripe;
  };

  // 支付按钮
  const handlePay = async (row: any, flag: boolean) => {
    // 防止重复点击
    if (loading || confirmLoading) {
      return;
    }
    if (flag) {
      setLoading(true);
    }
    if (isLogin) {
      const params: any = {
        item_id: row.item_id,
      };
      if (router.query?.url) {
        params.url = router.query?.url;
      }
      if (router.query.social_account_id) {
        params.social_account_id = router.query.social_account_id;
      }
      if (router.query.key) {
        params.key = router.query.key;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (window.Rewardful && window.Rewardful.referral) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        params.referral_id = window.Rewardful.referral;
      }
      // 侵权监控id 用于给付完费的用户发送邮件使
      if (risk_unique_id) {
        params.risk_unique_id = risk_unique_id;
      }
      orderSubsRequest(params).then(async (res) => {
        if (res.code == 0) {
          client_secret.current = res.data?.client_secret;
          // 检查 client_secret 是否有效（升级时可能为空，因为后端直接扣款）
          if (client_secret.current) {
            // 新购买：需要跳转 Stripe Checkout
            try {
              await loadScripePay();
            } catch (error) {
              console.error('Stripe redirect failed:', error);
            }
          }
          // 升级成功：轮询获取用户订阅信息，等待后端数据同步
          const pollForUpdate = async (expectedItemId: number, maxAttempts = 5) => {
            for (let i = 0; i < maxAttempts; i++) {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              const result = await getPersionalProductApi();
              if (result.data?.[0]?.item_id === expectedItemId) {
                return result;
              }
            }
            // 超过最大尝试次数，返回最后一次的结果
            return await getPersionalProductApi();
          };
          const updatedSubs = await pollForUpdate(row.item_id);
          if (updatedSubs.data?.length > 0) {
            setUserSubs(updatedSubs.data);
            setselectItem(updatedSubs.data[0]);
            if ([1, 3, 5].indexOf(updatedSubs.data[0]?.item_id) !== -1) {
              setTab(0);
            } else {
              setTab(1);
            }
            // 数据更新成功后再提示
            message.success(res.message);
          }
          // 关闭升级确认弹框
          setIsUpgradeModal(false);
        } else {
          // 支付失败
          message.warning(res.message);
          setIsPayFailModal(true);
        }
        setLoading(false);
        setConfirmLoading(false);
      });
    } else {
      setLoading(false);
      onOpenLoginModal(true);
    }
  };

  const loadScripePay = async () => {
    // ✅ 优化：在支付时动态加载 Stripe SDK
    const stripe = await loadStripeSDK();
    await stripe?.redirectToCheckout({
      sessionId: client_secret.current,
    });
    setLoading(false);
  };
  const handleSelect = (type: number) => {
    setTab(type);
  };
  useEffect(() => {
    if (tab == 0) {
      const arr = planList.filter((item) => item.data_type == 'Month');
      setnewPlanList(arr);
      // setselectItem(data.planList[0]);
    } else {
      const arr = planList.filter((item) => item.data_type == 'Year');
      setnewPlanList(arr);
      // setselectItem(data.planList[1]);
    }
  }, [data, tab]);
  useEffect(() => {
    // setselectItem({});
    const dom = planContainer.current;
    if (dom && dom.children?.length > 0 && isMobile) {
      const cw = dom.children[1]?.scrollWidth * 4 + 60;
      const clw = dom.children[1]?.scrollWidth;
      const cliWidth = window.innerWidth;
      let tempIndex = 1;
      newPlanList.forEach((item, index) => {
        if (item?.item_id == selectItem?.item_id) {
          tempIndex = index;
        }
      });
      const tempLeft = tempIndex * (clw + 30) + clw / 2 - cliWidth / 2 + 5;
      if (tempLeft <= 0) {
        dom.scrollLeft = 0;
      } else if (tempLeft >= cw - clw) {
        dom.scrollLeft = cw - clw;
      } else {
        dom.scrollLeft = tempLeft;
      }
    }
  }, [newPlanList, isMobile]);
  const freePlanMonitoringDescList = [
    {
      img: '/images/plan/right.webp',
      hml: `<p>${t('1 protected account')}</p>`,
      hml1: `<p>${t('1 protected account')}</p>`,
    },
    {
      img: '/images/plan/right.webp',
      hml: `<p>${t('Basic leak scanning (Google, Bing)')}</p>`,
      hml1: `<p>${t('Basic leak scanning (Google, Bing)')}</p>`,
    },
    {
      img: '/images/plan/right.webp',
      hml: `<p>${t('1x Complimentary Leak Scan')}</p>`,
      hml1: `<p>${t('1x Complimentary Leak Scan')}</p>`,
    },
    {
      img: '/images/plan/right.webp',
      hml: `<p>${t('US monitoring included')}</p>`,
      hml1: `<p>${t('US monitoring included')}</p>`,
    },
    {
      img: '/images/plan/error.webp',
      hml: `<p>${t('Upload links for removal')}</p>`,
      hml1: `<p>${t('Upload links for removal')}</p>`,
    },
  ];
  const freePlanTakedownDescList = [
    {
      img: '/images/plan/error.webp',
      hml: `<p>${t('No automatic removals')}</p>`,
      hml1: `<p>${t('No automatic removals')}</p>`,
    },
  ];
  // 三个套餐的 Monitoring & Detection 列表
  const planMonitoringDescList: any[] = [
    // Starter
    [
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Unlimited protected accounts')}</p>`,
        hml1: `<p>${t('Unlimited protected accounts')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Deep web leak scanning')}</p>`,
        hml1: `<p>${t('Deep web leak scanning')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Daily scans')}</p>`,
        hml1: `<p>${t('Daily scans')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('US & UK monitoring included')}</p>`,
        hml1: `<p>${t('US & UK monitoring included')}</p>`,
      },
      {
        img: '/images/plan/error.webp',
        hml: `<p>${t('Upload links for removal')}</p>`,
        hml1: `<p>${t('Upload links for removal')}</p>`,
      },
    ],
    // Pro
    [
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Unlimited protected accounts')}</p>`,
        hml1: `<p>${t('Unlimited protected accounts')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Deep web leak scanning')}</p>`,
        hml1: `<p>${t('Deep web leak scanning')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Daily scans')}</p>`,
        hml1: `<p>${t('Daily scans')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('US & UK + 1 additional country/region')}</p>`,
        hml1: `<p>${t('US & UK + 1 additional country/region')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Upload links for removal: 20/day')}</p>`,
        hml1: `<p>${t('Upload links for removal: 20/day')}</p>`,
      },
    ],
    // Ultimate
    [
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Unlimited protected accounts')}</p>`,
        hml1: `<p>${t('Unlimited protected accounts')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Deep web leak scanning')}</p>`,
        hml1: `<p>${t('Deep web leak scanning')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Daily scans')}</p>`,
        hml1: `<p>${t('Daily scans')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('US & UK + 3 additional countries/regions')}</p>`,
        hml1: `<p>${t('US & UK + 3 additional countries/regions')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Upload links for removal: Unlimited')}</p>`,
        hml1: `<p>${t('Upload links for removal: Unlimited')}</p>`,
      },
    ],
  ];
  // 三个套餐的 Takedown & Enforcement 列表
  const planTakedownDescList: any[] = [
    // Starter
    [
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Automated Removal from Google & Bing')}</p>`,
        hml1: `<p>${t('Automated Removal from Google & Bing')}</p>`,
      },
      {
        img: '/images/plan/error.webp',
        hml: `<p>${t('Removal from Social Media Platforms')}</p>`,
        hml1: `<p>${t('Removal from Social Media Platforms')}</p>`,
      },
      {
        img: '/images/plan/error.webp',
        hml: `<p>${t('Catfish & impersonator removals')}</p>`,
        hml1: `<p>${t('Catfish & impersonator removals')}</p>`,
      },
      {
        img: '/images/plan/error.webp',
        hml: `<p>${t('Telegram Takedown')}</p>`,
        hml1: `<p>${t('Telegram Takedown')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Daily Report')}</p>`,
        hml1: `<p>${t('Daily Report')}</p>`,
      },
    ],
    // Pro
    [
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Automated Removal from Google & Bing')}</p>`,
        hml1: `<p>${t('Automated Removal from Google & Bing')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Removal from Social Media Platforms')}</p>`,
        hml1: `<p>${t('Removal from Social Media Platforms')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Catfish & impersonator removals')}</p>`,
        hml1: `<p>${t('Catfish & impersonator removals')}</p>`,
      },
      {
        img: '/images/plan/error.webp',
        hml: `<p>${t('Telegram Takedown')}</p>`,
        hml1: `<p>${t('Telegram Takedown')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Daily Report')}</p>`,
        hml1: `<p>${t('Daily Report')}</p>`,
      },
    ],
    // Ultimate
    [
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Automated Removal from Google & Bing')}</p>`,
        hml1: `<p>${t('Automated Removal from Google & Bing')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Removal from Social Media Platforms')}</p>`,
        hml1: `<p>${t('Removal from Social Media Platforms')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Catfish & impersonator removals')}</p>`,
        hml1: `<p>${t('Catfish & impersonator removals')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Telegram Takedown')}</p>`,
        hml1: `<p>${t('Telegram Takedown')}</p>`,
      },
      {
        img: '/images/plan/right.webp',
        hml: `<p>${t('Daily Report')}</p>`,
        hml1: `<p>${t('Daily Report')}</p>`,
      },
    ],
  ];

  const isChange = (item_id: number) => {
    if (isLogin) {
      if (userSubs.length == 0) {
        return true;
      }
      let arr = [];
      if (tab == 1) {
        arr = [1, 2, 3, 4, 5, 6];
      } else {
        arr = [1, 3, 5, 2, 4, 6];
      }
      if (arr.indexOf(item_id) > arr.indexOf(userSubs[0]?.item_id)) {
        return true;
      } else if (
        arr.indexOf(item_id) == arr.indexOf(userSubs[0]?.item_id) &&
        new Date(userSubs[0]?.expire_time).getTime() < new Date().getTime()
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  // 判断按钮是否应该禁用（当会弹出警告时）
  const isButtonDisabled = (item: any) => {
    return !isChange(item.item_id);
  };
  const getPrice = (item: any) => {
    if (item.data_type == 'Month') {
      if (!isLogin || userUse?.first_buy) {
        if (isOff) {
          return item.item_id == 1 ? '69.3' : item.item_id == 3 ? '104.3' : '209.3';
        } else {
          return item.item_id == 1 ? '79' : item.item_id == 3 ? '119' : '239';
        }
      } else {
        return item.amount;
      }
    } else {
      return item.item_id == 2 ? '82.5' : item.item_id == 4 ? '123.16' : 249.16;
    }
  };

  const [isOff, setIsOff] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      let attempts = 0;
      const maxAttempts = 50; // 5秒的最大等待时间
      const checkRewardful = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (window.Rewardful && window.Rewardful.referral) {
          setIsOff(true);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          const timer = setTimeout(() => {
            clearTimeout(timer);
            checkRewardful();
          }, 100);
        }
      };

      checkRewardful();
    }
  }, []);

  // 操作弹窗
  const [isAddAccountModal, setIsAddAccountModal] = useState(false);

  return (
    <div className={styles.container}>
      <h1>{t('Pricing Plans')}</h1>
      <div className={styles.tab}>
        <div onClick={() => handleSelect(0)} style={{ color: tab == 0 ? 'var(--app-primary-color)' : '#000000' }}>
          {t('Monthly')}
        </div>
        <div onClick={() => handleSelect(1)} style={{ color: tab == 1 ? 'var(--app-primary-color)' : '#000000' }}>
          {t('Annually')}
        </div>
        <div className={styles.active} style={{ left: tab == 0 ? '3px' : 'calc(50% - 3px)' }}></div>
      </div>
      <div className={styles.desc}>
        {t('No Contracts. No Hidden Fees. Cancel Anytime.')}
        {/* <span>copycobrafast@gmail.com</span> */}
      </div>
      <div className={styles.main} ref={planContainer}>
        <div
          className={classNames({
            [styles.dis]: isLogin,
            [styles.disableBtnFirst]: !isLogin,
          })}
          style={{
            borderColor: !isLogin ? '#D9D9D9' : 'rgb(254, 248, 248)',
          }}
        >
          <div className={styles.title}>{t('Trial')}</div>
          <div className={styles.price}>
            <span>{t('Free')}</span>
          </div>
          {/* {
              [styles.acbtn]: isLogin,
            } */}

          {!(tab === 0 && isLogin && !userUse?.first_buy) && (
            <div className={styles.yearPrice} style={{}}>
              <span style={{ opacity: 0 }}>{t('Free')}</span>
            </div>
          )}
          <div className={styles.planDescription}>
            {t('For checking leak risk only — no removals or protection included.')}
          </div>
          <Button
            disabled={isLogin}
            className={classNames({
              [styles.acbtnFirst]: !isLogin,
              [styles.disableBtn]: isLogin,
            })}
            onClick={() => {
              if (!isLogin) {
                onOpenLoginModal(true);
              }
            }}
          >
            {userSubs.length == 0 ? t('Current plan') : t('Get started today')}
          </Button>
          <div className={styles.planInfo}>
            <div className={styles.planInfoTitle}>{t('Monitoring & Detection')}</div>
            {freePlanMonitoringDescList.map((it: any, idx: number) => (
              <div key={idx}>
                <img src={it.img} alt="" />
                <div
                  dangerouslySetInnerHTML={{
                    __html: tab == 0 ? it.hml : it.hml1,
                  }}
                ></div>
                <div>
                  {it.tootip && (
                    <Tooltip title={<span style={{ color: '#000000' }}>{it.tootip}</span>} color="#FFFFFF">
                      <Image src="/images/plan/verb.webp" height={15} width={15} alt="verb"></Image>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
            <div className={styles.planInfoTitle2}>{t('Takedown & Enforcement')}</div>
            {freePlanTakedownDescList.map((it: any, idx: number) => (
              <div key={idx}>
                <img src={it.img} alt="" />
                <div
                  dangerouslySetInnerHTML={{
                    __html: tab == 0 ? it.hml : it.hml1,
                  }}
                ></div>
                <div>
                  {it.tootip && (
                    <Tooltip title={<span style={{ color: '#000000' }}>{it.tootip}</span>} color="#FFFFFF">
                      <Image src="/images/plan/verb.webp" height={15} width={15} alt="verb"></Image>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {newPlanList.map((item, index) => (
          <div
            key={index}
            className={classNames({
              [styles.dis]: !isChange(item.item_id),
              [styles.active1]: item.item_id == selectItem?.item_id,
            })}
            style={{
              borderColor:
                isChange(item.item_id) || item.item_id == selectItem?.item_id ? '#D9D9D9' : 'rgb(254 248 248)',
            }}
            onMouseEnter={() => {
              if (isChange(item.item_id)) {
                setselectItem(item);
              }
            }}
          >
            <div className={styles.title}>{t(item.name)}</div>
            <div className={styles.price}>
              <span>${getPrice(item)}</span>
              <span>{t('/mo')}</span>
              {item.item_id == 2 && <span>{t('Save17%')}</span>}
              {item.item_id == 4 && <span>{t('Save17%')}</span>}
              {item.item_id == 6 && <span>{t('Save17%')}</span>}
              {item.item_id == 1 && (!isLogin || userUse?.first_buy) && (
                <span>{isOff ? t('30% Off First Month') : t('20% Off First Month')}</span>
              )}
              {item.item_id == 3 && (!isLogin || userUse?.first_buy) && (
                <span>{isOff ? t('30% Off First Month') : t('20% Off First Month')}</span>
              )}
              {item.item_id == 5 && (!isLogin || userUse?.first_buy) && (
                <span>{isOff ? t('30% Off First Month') : t('20% Off First Month')}</span>
              )}
            </div>
            <div
              className={styles.yearPrice}
              style={{
                textDecoration: item.data_type == 'Month' ? 'line-through' : '',
              }}
            >
              {item.data_type == 'Year' && (
                <div>
                  <span>${item.amount}</span>
                  <span>{t('/yr')}</span>
                </div>
              )}
              {item.data_type == 'Month' && (!isLogin || userUse?.first_buy) && (
                <div>
                  <span>${item.amount}</span>
                  <span>{t('/mo')}</span>
                </div>
              )}
            </div>
            <div className={styles.planDescription}>{t(getPlanDescription(item))}</div>
            {item.item_id == selectItem?.item_id ? (
              <Button
                disabled={isButtonDisabled(item)}
                className={classNames({
                  [styles.acbtnSelect]: isChange(item.item_id),
                  [styles.acbtn]: !isChange(item.item_id),
                  [styles.disabledBtn]: isButtonDisabled(item),
                })}
                onClick={() => {
                  if (isChange(item.item_id)) {
                    if (getBtndesc(item) == t('Upgrade Protection')) {
                      setIsUpgradeModal(true);
                    } else {
                      handlePay(item, true);
                    }
                  } else {
                    message.warning(t('you have already subscribed to this plan'));
                  }
                }}
                loading={loading}
              >
                {getBtndesc(item)}
              </Button>
            ) : (
              <Button
                disabled={isButtonDisabled(item)}
                className={classNames({
                  [styles.buybtn]: true,
                  [styles.disabledBtn]: isButtonDisabled(item),
                })}
                onClick={() => {
                  if (isChange(item.item_id)) {
                    if (getBtndesc(item) == t('Upgrade Protection')) {
                      setIsUpgradeModal(true);
                    } else {
                      handlePay(item, true);
                    }
                  } else {
                    message.warning(t('you have already subscribed to this plan'));
                  }
                }}
              >
                {getBtndesc(item)}
              </Button>
            )}
            <div className={styles.planInfo}>
              <div className={styles.planInfoTitle}>{t('Monitoring & Detection')}</div>
              {planMonitoringDescList[index].map((it: any, idx: number) => (
                <div key={idx}>
                  <img src={it.img} alt="" />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: tab == 0 ? it.hml : it.hml1,
                    }}
                  ></div>
                  <div>
                    {it.tootip && (
                      <Tooltip title={<span style={{ color: '#000000' }}>{it.tootip}</span>} color="#FFFFFF">
                        <Image src="/images/plan/verb.webp" height={15} width={15} alt="verb"></Image>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))}
              <div className={styles.planInfoTitle2}>{t('Takedown & Enforcement')}</div>
              {planTakedownDescList[index].map((it: any, idx: number) => (
                <div key={idx}>
                  <img src={it.img} alt="" />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: tab == 0 ? it.hml : it.hml1,
                    }}
                  ></div>
                  <div>
                    {it.tootip && (
                      <Tooltip title={<span style={{ color: '#000000' }}>{it.tootip}</span>} color="#FFFFFF">
                        <Image src="/images/plan/verb.webp" height={15} width={15} alt="verb"></Image>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <NeedHelp />
      <div className={styles.yearDesc}>
        {t(
          'Tip: After selecting a package and purchasing it successfully, it is deemed that you agree to our terms of service and subscription and refund policy. The system will automatically subscribe and automatically renew the membership package for the next cycle. You can cancel the subscription at any time. If the subscription is not cancelled in time and the amount is deducted, we do not support refunds',
        )}
      </div>
      {/* 添加账户 */}
      <AddAccountModal
        isModalOpen={isAddAccountModal}
        handleCancel={() => setIsAddAccountModal(false)}
      ></AddAccountModal>
      {/* 支付失败 */}
      <PayFailModal isModalOpen={isPayFailModal} handleClose={() => setIsPayFailModal(false)}></PayFailModal>
      {/* 升级确认弹窗 */}
      <ConfirmUpgradeModal
        isModalOpen={isUpgradaModal}
        handleCancel={() => {
          setIsUpgradeModal(false);
          setConfirmLoading(false);
        }}
        handleOk={() => {
          handlePay(selectItem, false);
          setConfirmLoading(true);
        }}
        loading={confirmLoading}
      ></ConfirmUpgradeModal>
    </div>
  );
};

// ✅ 优化：移除 payIndex 包装组件和 Elements Provider，Stripe SDK 改为动态加载
export default Index;
